import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DataService } from '../../services/dataService';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { downloadChartAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { standardTitle } from '../../utils/chartFormatting';
import './ChartLayout.css';
import { DataTable } from './DataTable';

Chart.register(...registerables);

export interface VMTMetrics {
  /** Single reporting year used consistently across headline, per-capita, and narrative. */
  reportingYear: number;
  currentVMT: number;
  /** Percent change from actual 2020 LDV VMT to reporting year; null when 2020 unavailable. */
  changeSince2020: number | null;
  changeSince2005?: number;
  californiaAverage: number;
  /** Annual light-duty VMT (PRIVATE_AUTO + ON_DEMAND_AUTO) for reportingYear. */
  reportingYearLdvVmt: number;
  /** @deprecated Use reportingYearLdvVmt — kept for transitional callers. */
  totalVMT2023: number;
  pctVMTFromLightDuty: number | null;
  /** LDV year used in LDV+HDV share (usually reportingYear). */
  ldvShareYear: number | null;
  /** HDV EMFAC year used when it differs from reportingYear (e.g. 2023 HDV with 2025 LDV). */
  ldvShareHdvYear: number | null;
  populationYear: number;
  populationValue: number;
  populationSource: string;
}

// VMTPerCapitaMetrics interface removed - no longer needed

interface VMTChartProps {
  location: string;
  geofips: number | null;
  chartView?: 'total' | 'percapita';
  emfac2005_2014Method?: 'EMFAC2025' | 'Population' | 'Combined';
  backcastingMethod?: 'AADT' | 'EMFAC2025' | 'EMFAC Fleet' | 'EMFAC2021 Fleet' | 'Combined';
  emfacFleetVersion?: '2021' | '2025';
  embedOnly?: boolean;
  onMetricsCalculated?: (metrics: VMTMetrics) => void;
  onDataCalculated?: (data: any[]) => void;
  // Expose data table functionality for parent ChartLayout
  onDataTableReady?: (content: React.ReactNode, toggleFn: () => void) => void;
  // Expose download functionality for parent ChartLayout
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
}

type VMTBackcastMethod2005 = 'EMFAC2025' | 'Population' | 'Combined';
type VMTBackcastMethod2015 =
  | 'AADT'
  | 'EMFAC2025'
  | 'EMFAC Fleet'
  | 'EMFAC2021 Fleet'
  | 'Combined';

type VMTDownloadRow = {
  year: number;
  vmt: number;
  data_source: string;
  location: string;
};

const VMT_COLOR_REPLICA = '#3b82f6';
const VMT_COLOR_BACKCAST_EARLY = '#f59e0b';
const VMT_COLOR_BACKCAST_LATE = '#10b981';
const VMT_COLOR_USER_MODIFIED = '#dc2626';
const VMT_COLOR_CUSTOM_SOURCE = '#9333ea';

const USER_MODIFIED_LEGEND_LABEL = 'User Modified Values';
const RATIO_ADJUSTED_SUFFIX = '/Ratio-Adjusted';

function isRatioAdjustedLabel(source: string): boolean {
  return source.endsWith(RATIO_ADJUSTED_SUFFIX);
}

function stripRatioAdjustedSuffix(source: string): string {
  return isRatioAdjustedLabel(source)
    ? source.slice(0, -RATIO_ADJUSTED_SUFFIX.length)
    : source;
}

function formatRatioAdjustedLabel(originalLabel: string): string {
  return `${originalLabel}${RATIO_ADJUSTED_SUFFIX}`;
}

/** EMFAC fleet HDV VMT is published through 2023; Replica LDV may be 2024–2025. */
const EMFAC_HDV_VMT_MAX_YEAR = 2023;

async function sumHdvVmtForGeofips(geofips: number, year: number): Promise<number> {
  const { data, error } = await supabaseClient
    .from('emfac2025_fleet')
    .select('total_vmt')
    .eq('geofips', geofips)
    .eq('class', 'HDV')
    .eq('year', year);
  if (error || !data?.length) return 0;
  return data.reduce((sum, row) => sum + (Number(row.total_vmt) || 0), 0);
}

async function latestHdvVmtYear(geofips: number, throughYear: number): Promise<{ year: number; vmt: number } | null> {
  const cap = Math.min(throughYear, EMFAC_HDV_VMT_MAX_YEAR);
  for (let y = cap; y >= 2015; y -= 1) {
    const vmt = await sumHdvVmtForGeofips(geofips, y);
    if (vmt > 0) return { year: y, vmt };
  }
  return null;
}

function isDerivedVMTSource(source: string): boolean {
  return source === USER_MODIFIED_LEGEND_LABEL || isRatioAdjustedLabel(source);
}

function buildReplicaLegendLabel(years: number[]): string {
  const replicaYears = years.filter((y) => y >= 2023).sort((a, b) => a - b);
  return replicaYears.length > 0 ? `Replica (${replicaYears.join(', ')})` : 'Replica';
}

function formatVMTDataSourceLabel(
  year: number,
  baseSource: string,
  replicaLabel: string
): string {
  if (baseSource === 'Replica' || year >= 2023) {
    return replicaLabel;
  }
  if (year >= 2005 && year <= 2014) {
    return `${baseSource} (2005-2014)`;
  }
  if (year >= 2015 && year <= 2022) {
    return `${baseSource} (2015-2022)`;
  }
  return baseSource;
}

function applyVMTDisplayLabels(rows: VMTDownloadRow[]): VMTDownloadRow[] {
  const replicaLabel = buildReplicaLegendLabel(rows.map((row) => row.year));
  return rows.map((row) => {
    const baseSource = resolveVMTBaseDataSource(row.data_source, row.year);
    return {
      ...row,
      data_source: formatVMTDataSourceLabel(row.year, baseSource, replicaLabel),
    };
  });
}

function resolveVMTBaseDataSource(dataSource: string, year: number): string {
  if (dataSource === USER_MODIFIED_LEGEND_LABEL) {
    return dataSource;
  }
  if (isRatioAdjustedLabel(dataSource)) {
    return resolveVMTBaseDataSource(stripRatioAdjustedSuffix(dataSource), year);
  }
  if (dataSource.startsWith('Replica')) {
    return 'Replica';
  }
  const periodMatch = dataSource.match(/^(.+)\s+\((2005-2014|2015-2022)\)$/);
  if (periodMatch) {
    return periodMatch[1];
  }
  if (dataSource) {
    return dataSource;
  }
  if (year >= 2023) {
    return 'Replica';
  }
  return dataSource;
}

function getRatioAdjustedYears(
  data: VMTDownloadRow[],
  original: VMTDownloadRow[],
  modifiedYears: Set<number>
): Set<number> {
  const scaled = new Set<number>();
  for (const row of data) {
    if (modifiedYears.has(row.year)) continue;
    const orig = original.find((d) => d.year === row.year);
    if (orig && Math.abs(row.vmt - orig.vmt) > 1) {
      scaled.add(row.year);
    }
  }
  return scaled;
}

function buildTableDisplayData(
  finalData: VMTDownloadRow[],
  original: VMTDownloadRow[],
  modifiedYears: Set<number>
): VMTDownloadRow[] {
  return finalData.map((row) => {
    if (modifiedYears.has(row.year)) {
      return { ...row, data_source: USER_MODIFIED_LEGEND_LABEL };
    }
    const orig = original.find((d) => d.year === row.year);
    if (orig && Math.abs(row.vmt - orig.vmt) > 1) {
      return { ...row, data_source: formatRatioAdjustedLabel(orig.data_source) };
    }
    return orig ? { ...row, data_source: orig.data_source } : row;
  });
}

function resolveVMTDataSource(
  year: number,
  emfac2005_2014Method: VMTBackcastMethod2005,
  backcastingMethod: VMTBackcastMethod2015
): string {
  if (year === 2023 || year === 2024 || year === 2025) {
    return 'Replica';
  }
  if (year >= 2005 && year <= 2014) {
    if (emfac2005_2014Method === 'Combined') return 'Combined Backcasted';
    if (emfac2005_2014Method === 'Population') return 'Population Backcasted';
    return 'EMFAC2025 Backcasted';
  }
  if (backcastingMethod === 'Combined') return 'Combined Backcasted';
  if (backcastingMethod === 'EMFAC Fleet') return 'EMFAC Fleet Backcasted';
  if (backcastingMethod === 'EMFAC2021 Fleet') return 'EMFAC2021 Fleet Backcasted';
  if (backcastingMethod === 'EMFAC2025') return 'EMFAC2025 Backcasted';
  return 'AADT Backcasted';
}

function vmtColorForDataSource(source: string, year: number): string {
  const label = stripRatioAdjustedSuffix(source);
  if (label === USER_MODIFIED_LEGEND_LABEL) return VMT_COLOR_USER_MODIFIED;
  if (label.startsWith('Replica')) return VMT_COLOR_REPLICA;
  if (label.includes('(2005-2014)')) return VMT_COLOR_BACKCAST_EARLY;
  if (label.includes('(2015-2022)')) return VMT_COLOR_BACKCAST_LATE;
  if (label === 'Replica' || year >= 2023) return VMT_COLOR_REPLICA;
  if (year >= 2005 && year <= 2014) return VMT_COLOR_BACKCAST_EARLY;
  return VMT_COLOR_BACKCAST_LATE;
}

function legendLabelSortKey(label: string): number {
  const base = stripRatioAdjustedSuffix(label);
  let key = 10;
  if (base.includes('(2005-2014)')) key = 1;
  else if (base.includes('(2015-2022)')) key = 2;
  else if (base.startsWith('Replica')) key = 3;
  else if (base === USER_MODIFIED_LEGEND_LABEL) key = 5;
  return isRatioAdjustedLabel(label) ? key + 0.05 : key;
}

function vmtBorderColor(backgroundColor: string): string {
  const map: Record<string, string> = {
    [VMT_COLOR_REPLICA]: '#1e40af',
    [VMT_COLOR_BACKCAST_EARLY]: '#d97706',
    [VMT_COLOR_BACKCAST_LATE]: '#047857',
    [VMT_COLOR_USER_MODIFIED]: '#b91c1c',
    [VMT_COLOR_CUSTOM_SOURCE]: '#7e22ce',
  };
  return map[backgroundColor] ?? backgroundColor;
}

function buildVMTLegendLabels(data: VMTDownloadRow[]) {
  const uniqueLabels = Array.from(
    new Set(data.map((row) => row.data_source).filter(Boolean))
  ).sort((a, b) => legendLabelSortKey(a) - legendLabelSortKey(b));

  return uniqueLabels.map((text) => {
    const sampleYear = data.find((row) => row.data_source === text)?.year ?? 0;
    const color = vmtColorForDataSource(text, sampleYear);
    return {
      text,
      fillStyle: color,
      strokeStyle: color,
      lineWidth: 1,
      pointStyle: 'rect' as const,
    };
  });
}

export const VMTChart: React.FC<VMTChartProps> = ({ location, geofips, chartView = 'total', emfac2005_2014Method = 'EMFAC2025', backcastingMethod = 'AADT', emfacFleetVersion = '2025', embedOnly = false, onMetricsCalculated, onDataCalculated, onDataTableReady, onDownloadPNGReady, onDownloadCSVReady }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [showDataTable, setShowDataTable] = useState(false);
  const [modifiedYears, setModifiedYears] = useState<Set<number>>(new Set());
  const chartCreationInProgress = useRef(false);


  // Function to calculate California average per capita VMT using EMFAC2025_county_filtered
  const calculateCaliforniaPerCapitaVMT = async (years: number[]) => {
    try {
      // Fetch California population data
      const { data: californiaPopData, error: popError } = await supabaseClient
        .from('population')
        .select('year, population')
        .eq('geofips', 6) // California state FIPS code (no leading zero)
        .eq('geography_type', 'State')
        .in('year', years)
        .order('year', { ascending: true });

      if (popError) {
        console.error('Error fetching California population data:', popError);
        return years.map(() => 0);
      }

      // Fetch all county-level VMT data from EMFAC2025_county_filtered
      const { data: countyVMTData, error: vmtError } = await supabaseClient
        .from('emfac2025_county_filtered')
        .select('year, total_vmt')
        .in('year', years)
        .order('year', { ascending: true });

      if (vmtError) {
        console.error('Error fetching California county VMT data:', vmtError);
        return years.map(() => 0);
      }

      // Sum VMT by year across all counties to get California total VMT
      const californiaVMTByYear = new Map<number, number>();
      (countyVMTData || []).forEach((record: any) => {
        const year = record.year;
        const vmt = parseFloat(record.total_vmt) || 0;
        if (californiaVMTByYear.has(year)) {
          californiaVMTByYear.set(year, californiaVMTByYear.get(year)! + vmt);
        } else {
          californiaVMTByYear.set(year, vmt);
        }
      });

      // Calculate per capita VMT for each year
      const perCapitaData = years.map(year => {
        const yearPopData = californiaPopData?.find(d => d.year === year);
        if (!yearPopData) {
          return null;
        }

        const totalVMT = californiaVMTByYear.get(year) || 0;
        if (totalVMT === 0) {
          return null;
        }

        const population = yearPopData.population || 1;
        const perCapita = (totalVMT / population) / 365; // Daily per capita VMT
        return perCapita;
      });

      return perCapitaData;
    } catch (error) {
      console.error('Error calculating California per capita VMT:', error);
      return years.map(() => 0);
    }
  };

  // Effect to ensure chart is created after component mounts
  useEffect(() => {
    // This effect runs after the component mounts and the canvas is available
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  useEffect(() => {
    const fetchVMTData = async () => {
      if (!location) {
        return;
      }
      
      if (!geofips) {
        setLoading(true);
        setError('Loading location data...');
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch raw Replica data, AADT backcasted data, and EMFAC backcasting data
        
        // Fetch data with error handling for EMFAC
        let emfacData: any[] = [];
        let rawVmtData: any[] = [];
        let backcastedVmtData: any[] = [];
        
        try {
          
          // Determine emfacVersion and emfacFleetVersion based on backcasting method
          // EMFAC2021 Fleet: use 2021 for both (no fallback to 2025)
          // EMFAC Fleet or EMFAC2025: use 2025 for both (no fallback to 2021)
          // Combined: use 2025 for county, but both fleet versions are used
          let emfacVersion: '2021' | '2025' = '2025';
          let fleetVersion: '2021' | '2025' = emfacFleetVersion;
          
          if (backcastingMethod === 'EMFAC2021 Fleet') {
            emfacVersion = '2021';
            fleetVersion = '2021';
          } else if (backcastingMethod === 'EMFAC Fleet' || backcastingMethod === 'EMFAC2025') {
            emfacVersion = '2025';
            fleetVersion = '2025';
          } else if (backcastingMethod === 'Combined') {
            // Combined uses 2025 for county, but both fleet versions
            emfacVersion = '2025';
            // fleetVersion stays as emfacFleetVersion (could be either)
          }
          
          const [rawVmtDataResult, backcastedVmtDataResult, emfacDataResult] = await Promise.all([
            DataService.getVMTData(location, undefined, geofips), // Raw Replica data using geofips
            DataService.getVMTDataWithScaling(geofips, backcastingMethod, emfacVersion, fleetVersion), // Backcasted data using selected method with correct version (no fallbacks)
            DataService.getEMFACBackcastingDataByGeofips(geofips, [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014], emfac2005_2014Method).catch(() => {
              return [];
            }) // EMFAC backcasting data using geofips with selected version
          ]);
          
          rawVmtData = rawVmtDataResult;
          backcastedVmtData = backcastedVmtDataResult;
          emfacData = emfacDataResult;
        } catch (error) {
          console.error('Error fetching VMT data:', error);
          setError('Failed to load VMT data');
          setLoading(false);
          return;
        }
        
        // Population by year for per-capita chart and reporting-year metrics (same-year, not projected)
        const populationByYear = new Map<number, number>();
        let populationSource = 'population table (geofips)';
        if (geofips) {
          try {
            const { data: popRows } = await supabaseClient
              .from('population')
              .select('year, population, geography_type')
              .eq('geofips', geofips)
              .gte('year', 2005)
              .lte('year', 2030)
              .not('population', 'is', null)
              .order('year', { ascending: true });
            if (popRows?.length) {
              for (const row of popRows) {
                if (row.geography_type === 'City' || !populationByYear.has(row.year)) {
                  populationByYear.set(row.year, row.population);
                }
              }
            }
          } catch {
            // fall through to location-name lookup
          }
        }
        if (populationByYear.size === 0) {
          try {
            const popSeries = await supabaseLocationService.getPopulationData(location);
            popSeries.forEach((row) => {
              if (row.population != null) populationByYear.set(row.year, row.population);
            });
            populationSource = 'population table (location_name)';
          } catch {
            // Population data not available
          }
        }
        const populationForYear = (year: number): number | null => populationByYear.get(year) ?? null;
        
        // Filter to only include PRIVATE_AUTO and ON_DEMAND modes for both datasets
        const rawPrivateAuto = rawVmtData.filter(d => d.mode === 'PRIVATE_AUTO');
        const rawOnDemand = rawVmtData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        const backcastedPrivateAuto = backcastedVmtData.filter(d => d.mode === 'PRIVATE_AUTO');
        const backcastedOnDemand = backcastedVmtData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        
        // Process EMFAC data for backcasting (2005-2014 only)
        
        // Get 2015 VMT data as the baseline for EMFAC backcasting
        const vmt2015PrivateAuto = backcastedPrivateAuto.find(d => d.year === 2015);
        const vmt2015OnDemand = backcastedOnDemand.find(d => d.year === 2015);
        const vmt2015Total = (vmt2015PrivateAuto?.vmt || 0) + (vmt2015OnDemand?.vmt || 0);
        
        
        // Create EMFAC backcasted data for years 2005-2014 only
        // Backcasting ratios are: current_year_vmt / next_year_vmt
        // So we need to apply them sequentially, starting from 2015 and working backwards
        const emfacBackcastedData: any[] = [];
        const emfacYears = [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014];
        
        if (vmt2015Total > 0 && emfacData.length > 0) {
          // Sort ratios by year descending (2014, 2013, ..., 2005)
          // This ensures we apply ratios sequentially from 2015 backwards
          const sortedRatios = emfacData
            .filter(r => emfacYears.includes(r.year))
            .sort((a, b) => b.year - a.year);
          
          // Calculate VMT for each year sequentially
          // Start with 2015 as baseline, then apply each ratio to get the previous year
          const vmtByYear = new Map<number, number>();
          vmtByYear.set(2015, vmt2015Total);
          
          // Apply ratios sequentially: year Y = year Y+1 * ratio_Y
          // where ratio_Y = VMT_Y / VMT_Y+1
          for (const emfacRecord of sortedRatios) {
            const nextYear = emfacRecord.year + 1;
            const nextYearVMT = vmtByYear.get(nextYear);
            
            if (nextYearVMT && emfacRecord.ratio) {
              // Apply ratio: current_year = next_year * ratio
              // Since ratio = current_vmt / next_vmt, we get: current = next * ratio
              const currentYearVMT = nextYearVMT * emfacRecord.ratio;
              vmtByYear.set(emfacRecord.year, currentYearVMT);
            }
          }
          
          // Now create the data points for each year, distributing between PRIVATE_AUTO and ON_DEMAND
          // Use the same proportion as 2015 for all years
          const privateAutoRatio = vmt2015PrivateAuto ? vmt2015PrivateAuto.vmt / vmt2015Total : 0.8; // Default to 80% private auto
          const onDemandRatio = 1 - privateAutoRatio;
          
          for (const year of emfacYears) {
            const yearVMT = vmtByYear.get(year);
            if (yearVMT !== undefined && yearVMT > 0) {
              emfacBackcastedData.push({
                location_name: location,
                year: year,
                mode: 'PRIVATE_AUTO',
                vmt: yearVMT * privateAutoRatio
              });
              
              emfacBackcastedData.push({
                location_name: location,
                year: year,
                mode: 'ON_DEMAND_AUTO',
                vmt: yearVMT * onDemandRatio
              });
            }
          }
        }
        
        
        if (rawPrivateAuto.length === 0 && rawOnDemand.length === 0) {
          setError('No VMT data available for this location');
          setLoading(false);
          return;
        }

        // Get all available years (combine EMFAC backcasted years 2005-2014 with AADT backcasted years 2015-2023)
        const emfacPrivateAuto = emfacBackcastedData.filter(d => d.mode === 'PRIVATE_AUTO');
        const emfacOnDemand = emfacBackcastedData.filter(d => d.mode === 'ON_DEMAND_AUTO');
        
        const allYears = Array.from(new Set([
          ...emfacPrivateAuto.map(d => d.year),
          ...emfacOnDemand.map(d => d.year),
          ...backcastedPrivateAuto.map(d => d.year),
          ...backcastedOnDemand.map(d => d.year),
          ...rawPrivateAuto.map(d => d.year),
          ...rawOnDemand.map(d => d.year)
        ])).sort();
        
        // Create a single dataset with different colors for different data sources
        const combinedData = allYears.map(year => {
          if (year === 2023 || year === 2024 || year === 2025) {
            // Use Replica data for 2023, 2024, and 2025
            const privateAutoVmt = rawPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = rawOnDemand.find(d => d.year === year)?.vmt || 0;
            return privateAutoVmt + onDemandVmt;
          } else if (year >= 2005 && year <= 2014) {
            // Use EMFAC backcasted data for 2005-2014
            const privateAutoVmt = emfacPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = emfacOnDemand.find(d => d.year === year)?.vmt || 0;
            const totalVmt = privateAutoVmt + onDemandVmt;
            return totalVmt;
          } else {
            // Use AADT backcasted data for 2015-2022
            const privateAutoVmt = backcastedPrivateAuto.find(d => d.year === year)?.vmt || 0;
            const onDemandVmt = backcastedOnDemand.find(d => d.year === year)?.vmt || 0;
            return privateAutoVmt + onDemandVmt;
          }
        });
        
        // Store chart data for download (labels reflect selected backcasting methods)
        const downloadData = applyVMTDisplayLabels(
          allYears.map((year, index) => ({
            year,
            vmt: Math.round(combinedData[index]),
            data_source: resolveVMTDataSource(year, emfac2005_2014Method, backcastingMethod),
            location,
          }))
        );

        const backgroundColors = downloadData.map((d) => vmtColorForDataSource(d.data_source, d.year));
        const borderColors = backgroundColors.map(vmtBorderColor);

        // Per-capita uses same-year population where available
        const perCapitaData = allYears.map((year, index) => {
          const pop = populationForYear(year);
          const vmt = combinedData[index];
          return pop && pop > 0 ? vmt / pop / 365 : null;
        });
        
        // Create datasets based on chart view
        let datasets: any[] = [];
        
        if (chartView === 'total') {
          // Total VMT view - only show VMT bars
          datasets = [
            {
              label: 'VMT Data',
              data: combinedData,
              backgroundColor: backgroundColors,
              borderColor: borderColors,
              borderWidth: 1,
              yAxisID: 'y'
            }
          ];
        } else {
          // Per capita view - show line chart comparing location to California average
          // Calculate California average per capita VMT
          const californiaPerCapitaData = await calculateCaliforniaPerCapitaVMT(allYears);
          
          datasets = [
            {
              label: location,
              data: perCapitaData,
              type: 'line',
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              borderWidth: 3,
              fill: false,
              pointRadius: 6,
              pointHoverRadius: 8,
              tension: 0.4
            },
            {
              label: 'California Average',
              data: californiaPerCapitaData,
              type: 'line',
              borderColor: '#ef4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderWidth: 3,
              fill: false,
              pointRadius: (context: any) => {
                // Don't show points for null values (years 2005-2018)
                return (context.parsed.y === null || context.parsed.y === undefined) ? 0 : 6;
              },
              pointHoverRadius: 8,
              tension: 0.4,
              spanGaps: false // Don't draw lines through null values
            }
          ];
        }

        setChartData(downloadData);
        setTableData(downloadData);
        setOriginalData([...downloadData]); // Store a copy of original data
        
        // Notify parent component of data change
        if (onDataCalculated) {
          onDataCalculated(downloadData);
        }

        
        if (datasets.length === 0) {
          setError('No VMT data available for the requested modes');
          setLoading(false);
          return;
        }

        // Create chart with proper timing
        const createChart = () => {
          if (chartCreationInProgress.current) {
            return;
          }
          
          if (!chartRef.current) {
            setTimeout(createChart, 100);
            return;
          }
          
          const ctx = chartRef.current.getContext('2d');
          if (!ctx) {
            setTimeout(createChart, 100);
            return;
          }
          
          chartCreationInProgress.current = true;

          // Destroy existing chart
          if (chartInstance.current) {
            chartInstance.current.destroy();
          }


          try {
            chartInstance.current = new Chart(ctx, {
              type: chartView === 'total' ? 'bar' : 'line',
              data: {
                labels: allYears.map(year => year.toString()),
                datasets: datasets
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                layout: {
                  padding: {
                    left: 10,
                    right: 10,
                    top: 10,
                    bottom: 10
                  }
                },
                plugins: {
                  title: embedOnly
                    ? { display: false }
                    : standardTitle(
                        `Vehicle Miles Traveled (${chartView === 'total' ? 'Light-duty' : 'Per Capita'}) - ${location}`
                      ),
                  legend: {
                    display: !embedOnly,
                    position: 'bottom',
                    labels: {
                      usePointStyle: true,
                      padding: 10,
                      generateLabels: function(chart: any) {
                        const labels: any[] = [];
                        
                        if (chartView === 'total') {
                          return buildVMTLegendLabels(downloadData);
                        } else {
                          // Per capita view - show location and California average
                          labels.push({
                            text: location,
                            fillStyle: '#3b82f6',
                            strokeStyle: '#3b82f6',
                            lineWidth: 3,
                            pointStyle: 'circle' as const,
                            hidden: false,
                            index: 0
                          });
                          labels.push({
                            text: 'California Average',
                            fillStyle: '#ef4444',
                            strokeStyle: '#ef4444',
                            lineWidth: 3,
                            pointStyle: 'circle' as const,
                            hidden: false,
                            index: 1
                          });
                        }
                        return labels;
                      }
                    }
                  }
                },
                scales: {
                  x: {
                    title: {
                      display: true,
                      text: 'Year',
                      font: {
                        weight: ('bold' as const)
                      }
                    },
                    grid: {
                      display: false
                    }
                  },
                  y: {
                    type: 'linear',
                    display: true,
                    position: 'left',
                    title: {
                      display: true,
                      text: chartView === 'total' ? 'Vehicle VMT (miles)' : 'Daily Vehicle VMT per Capita (miles)',
                      font: {
                        weight: ('bold' as const)
                      }
                    },
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.1)'
                    },
                    ticks: chartView === 'percapita' ? {
                      callback: function(value: any) {
                        return value.toFixed(1);
                      }
                    } : {
                      callback: function(value: any) {
                        // Format numbers with commas for thousands
                        if (typeof value === 'number') {
                          return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
                        }
                        return value;
                      }
                    }
                  }
                },
                interaction: {
                  intersect: false,
                  mode: 'index'
                }
              }
            });

            chartCreationInProgress.current = false;
          } catch (chartError) {
            console.error('Error creating chart:', chartError);
            setError('Failed to create chart');
            setLoading(false);
            chartCreationInProgress.current = false;
            return;
          }
        };

        // Start chart creation
        createChart();


        // Metrics: one explicit reporting year; change since 2020 uses actual 2020 (not 2005)
        if (onMetricsCalculated && combinedData.length > 0 && allYears.length > 0) {
          const replicaCandidateYears = allYears.filter((y) => y === 2023 || y === 2024 || y === 2025);
          const reportingYear =
            replicaCandidateYears.length > 0
              ? Math.max(...replicaCandidateYears)
              : Math.max(...allYears.filter((y, i) => combinedData[i] > 0));
          const idxReporting = allYears.indexOf(reportingYear);
          const reportingLdvVmt = idxReporting >= 0 ? combinedData[idxReporting] : 0;
          const reportingPop = populationForYear(reportingYear);
          const perCapitaReporting =
            reportingPop && reportingPop > 0 ? reportingLdvVmt / reportingPop / 365 : 0;

          const idx2020 = allYears.indexOf(2020);
          const vmt2020 = idx2020 >= 0 ? combinedData[idx2020] : null;
          const changeSince2020 =
            vmt2020 != null && vmt2020 > 0
              ? ((reportingLdvVmt - vmt2020) / vmt2020) * 100
              : null;

          const idx2005 = allYears.indexOf(2005);
          const vmt2005 = idx2005 >= 0 ? combinedData[idx2005] : null;
          const changeSince2005 =
            vmt2005 != null && vmt2005 > 0
              ? ((reportingLdvVmt - vmt2005) / vmt2005) * 100
              : undefined;

          let californiaAverage = 15.8; // Default hardcoded value
          try {
            const { data: californiaVMTData, error: caError } = await supabaseClient
              .from('replica_vmt_by_mode')
              .select('year, mode, vmt')
              .eq('geofips', 6)
              .order('year', { ascending: false })
              .limit(20);
            if (!caError && californiaVMTData && californiaVMTData.length > 0) {
              const latestYear = Math.max(...californiaVMTData.map(d => d.year));
              const californiaLatest = californiaVMTData.filter(d => d.year === latestYear);
              const californiaPrivateAuto = californiaLatest.filter(d => d.mode === 'PRIVATE_AUTO');
              const californiaOnDemand = californiaLatest.filter(d => d.mode === 'ON_DEMAND_AUTO');
              const californiaPrivateAutoVMT = californiaPrivateAuto.length > 0 ? californiaPrivateAuto[0].vmt : 0;
              const californiaOnDemandVMT = californiaOnDemand.length > 0 ? californiaOnDemand[0].vmt : 0;
              const californiaTotalVMT = californiaPrivateAutoVMT + californiaOnDemandVMT;
              let californiaPopulation = 39000000;
              try {
                const californiaData = await supabaseLocationService.getLatestPopulation('California');
                if (californiaData && californiaData.population) californiaPopulation = californiaData.population;
              } catch (err) {}
              californiaAverage = (californiaTotalVMT / californiaPopulation) / 365;
            }
          } catch (err) {}

          // LDV share: reporting-year LDV (Combined) vs latest EMFAC HDV VMT ≤ reporting year (through 2023).
          let pctVMTFromLightDuty: number | null = null;
          let ldvShareYear: number | null = null;
          let ldvShareHdvYear: number | null = null;
          if (geofips && idxReporting >= 0 && reportingLdvVmt > 0) {
            try {
              const hdvResolved = await latestHdvVmtYear(geofips, reportingYear);
              if (hdvResolved && hdvResolved.vmt > 0) {
                const totalMiles = reportingLdvVmt + hdvResolved.vmt;
                pctVMTFromLightDuty = Math.round((reportingLdvVmt / totalMiles) * 100);
                ldvShareYear = reportingYear;
                ldvShareHdvYear = hdvResolved.year;
              }
            } catch (err) {
              console.warn('VMTChart: LDV share (LDV+HDV) failed', err);
            }
          }

          const metrics: VMTMetrics = {
            reportingYear,
            currentVMT: Math.round(perCapitaReporting * 10) / 10,
            changeSince2020:
              changeSince2020 != null ? Math.round(changeSince2020 * 10) / 10 : null,
            changeSince2005:
              changeSince2005 != null ? Math.round(changeSince2005 * 10) / 10 : undefined,
            californiaAverage: Math.round(californiaAverage * 10) / 10,
            reportingYearLdvVmt: Math.round(reportingLdvVmt),
            totalVMT2023: Math.round(reportingLdvVmt),
            pctVMTFromLightDuty,
            ldvShareYear,
            ldvShareHdvYear,
            populationYear: reportingYear,
            populationValue: reportingPop ?? 0,
            populationSource,
          };
          onMetricsCalculated(metrics);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching VMT data:', err);
        setError('Failed to load VMT data');
        setLoading(false);
      }
    };

    fetchVMTData();
  }, [location, geofips, chartView, emfac2005_2014Method, backcastingMethod, emfacFleetVersion, embedOnly, onMetricsCalculated, onDataCalculated]);

  const toggleDataTable = useCallback(() => {
    setShowDataTable(!showDataTable);
  }, [showDataTable]);

  // Define handlers before early returns so they can be used in hooks
  const scaleDataToModifiedValue = useCallback((data: any[], modifiedYear: number, modifiedVMT: number) => {
    const originalModifiedData = originalData.find(d => d.year === modifiedYear);
    if (!originalModifiedData) return data;

    const scalingFactor = modifiedVMT / originalModifiedData.vmt;
    
    return data.map(item => {
      // Don't scale if this year has been manually modified
      if (modifiedYears.has(item.year)) {
        return item;
      }
      
      const originalItem = originalData.find(d => d.year === item.year);
      if (!originalItem) return item;
      
      return {
        ...item,
        vmt: Math.round(originalItem.vmt * scalingFactor)
      };
    });
  }, [originalData, modifiedYears]);

  // Define updateChart before handleDataTableChange to avoid temporal dead zone error
  const updateChart = useCallback((data: any[]) => {
    if (chartInstance.current && data.length > 0) {
      const sortedData = data.sort((a, b) => a.year - b.year);
      const years = sortedData.map(d => d.year);
      const vmtValues = sortedData.map(d => Math.round(d.vmt));
      
      const getDataSourceColor = (dataPoint: VMTDownloadRow) => {
        const originalPoint = originalData.find((orig) => orig.year === dataPoint.year);

        if (!originalPoint) {
          return VMT_COLOR_USER_MODIFIED;
        }

        if (modifiedYears.has(dataPoint.year)) {
          return VMT_COLOR_USER_MODIFIED;
        }

        if (
          originalPoint &&
          Math.abs(dataPoint.vmt - originalPoint.vmt) > 1
        ) {
          return vmtColorForDataSource(originalPoint.data_source, dataPoint.year);
        }

        if (originalPoint.data_source !== dataPoint.data_source) {
          return VMT_COLOR_CUSTOM_SOURCE;
        }

        return vmtColorForDataSource(dataPoint.data_source, dataPoint.year);
      };

      const backgroundColors = sortedData.map(getDataSourceColor);
      const borderColors = backgroundColors.map(vmtBorderColor);

      const hasCustomData = backgroundColors.some((color) => color === VMT_COLOR_CUSTOM_SOURCE);

      chartInstance.current.data = {
        labels: years,
        datasets: [{
          label: 'Vehicle Miles Traveled (Annual)',
          data: vmtValues,
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 1
        }]
      };

      if (chartInstance.current.options.plugins) {
        chartInstance.current.options.plugins.legend = {
        display: true,
        position: 'bottom',
        labels: {
          generateLabels: function() {
            const displayData = buildTableDisplayData(sortedData, originalData, modifiedYears);
            const legendLabels = buildVMTLegendLabels(displayData);
            if (hasCustomData) {
              legendLabels.push({
                text: 'Custom Data Source',
                fillStyle: VMT_COLOR_CUSTOM_SOURCE,
                strokeStyle: VMT_COLOR_CUSTOM_SOURCE,
                lineWidth: 1,
                pointStyle: 'rect',
              });
            }
            return legendLabels;
          }
        }
        };
      }
      
      chartInstance.current.update('none'); // Update without animation for better performance
    }
  }, [originalData, modifiedYears]);

  const handleDataTableChange = useCallback((updatedData: any[]) => {
    // Track which years have been manually modified and find the changed value
    const newModifiedYears = new Set(modifiedYears);
    let modifiedYear: number | null = null;
    let modifiedVMT: number | null = null;

    // Find which year was just modified by comparing to current table data
    updatedData.forEach((newItem) => {
      const currentItem = tableData.find((d) => d.year === newItem.year);
      if (currentItem && Math.abs(newItem.vmt - currentItem.vmt) > 1) {
        newModifiedYears.add(newItem.year);
        modifiedYear = newItem.year;
        modifiedVMT = newItem.vmt;
      }
    });

    // Scale all unmodified years based on the modified value
    let finalData = updatedData;
    if (modifiedYear !== null && modifiedVMT !== null) {
      finalData = scaleDataToModifiedValue(updatedData, modifiedYear, modifiedVMT);
    }

    const tableDisplayData = buildTableDisplayData(finalData, originalData, newModifiedYears);

    setModifiedYears(newModifiedYears);
    setTableData(tableDisplayData);
    setChartData(finalData);

    // Chart uses scaled values; colors follow modifiedYears only
    updateChart(finalData);

    if (onDataCalculated) {
      onDataCalculated(finalData);
    }
  }, [tableData, modifiedYears, scaleDataToModifiedValue, onDataCalculated, updateChart, originalData]);

  const handleRestoreDefaults = useCallback(() => {
    if (originalData.length > 0) {
      const restoredData = [...originalData];
      setTableData(restoredData);
      setChartData(restoredData);
      setModifiedYears(new Set()); // Reset modified years tracking
      
      // Update the chart with original data
      if (chartInstance.current) {
        const sortedData = restoredData.sort((a, b) => a.year - b.year);
        const years = sortedData.map(d => d.year);
        const vmtValues = sortedData.map(d => Math.round(d.vmt));
        
        const backgroundColors = restoredData.map((d) => vmtColorForDataSource(d.data_source, d.year));
        const borderColors = backgroundColors.map(vmtBorderColor);

        chartInstance.current.data = {
          labels: years,
          datasets: [{
            label: 'Vehicle Miles Traveled (Annual)',
            data: vmtValues,
            backgroundColor: backgroundColors,
            borderColor: borderColors,
            borderWidth: 1
          }]
        };

        if (chartInstance.current.options.plugins) {
          chartInstance.current.options.plugins.legend = {
          display: !embedOnly,
          position: 'bottom',
          labels: {
            generateLabels: function() {
              return buildVMTLegendLabels(sortedData);
            }
          }
          };
        }
        
        chartInstance.current.update('none');
      }
      
      // Notify parent component of data change
      if (onDataCalculated) {
        onDataCalculated(restoredData);
      }
    }
  }, [originalData, embedOnly, onDataCalculated]);

  // Memoize data table content so it can be used in useEffect
  const scaledYears = useMemo(
    () => getRatioAdjustedYears(tableData, originalData, modifiedYears),
    [tableData, originalData, modifiedYears]
  );

  const dataTableContent = useMemo(() => {
    if (tableData.length === 0) return null;
    
    return (
      <div>
        {(modifiedYears.size > 0 || scaledYears.size > 0) && (
          <div className="modified-years-info" style={{ marginBottom: '16px' }}>
            <small>
              {modifiedYears.size > 0 && (
                <>
                  <strong>User modified:</strong> {Array.from(modifiedYears).sort().join(', ')}
                </>
              )}
              {modifiedYears.size > 0 && scaledYears.size > 0 ? ' · ' : null}
              {scaledYears.size > 0 && (
                <>
                  <strong>Ratio-adjusted:</strong> scaled years show as original source plus “/Ratio-Adjusted”
                </>
              )}
            </small>
          </div>
        )}
        
        <DataTable
          data={tableData}
          highlightedRows={modifiedYears}
          highlightKey="year"
          columns={[
            { key: 'year', label: 'Year', type: 'year', editable: true, 
              validate: (value) => {
                const year = parseInt(value);
                if (isNaN(year) || year < 1990 || year > 2050) {
                  return 'Year must be between 1990 and 2050';
                }
                return null;
              }
            },
            { key: 'vmt', label: 'VMT (Annual)', type: 'number', editable: true,
              format: (value) => typeof value === 'number' ? Math.round(value).toLocaleString() : value,
              validate: (value) => {
                const vmt = parseFloat(value);
                if (isNaN(vmt) || vmt < 0) {
                  return 'VMT must be a positive number';
                }
                return null;
              }
            },
            { key: 'data_source', label: 'Data Source', type: 'text', editable: false },
            { key: 'location', label: 'Location', type: 'text', editable: false }
          ]}
          onDataChange={handleDataTableChange}
          onRestoreDefaults={handleRestoreDefaults}
          title="VMT Data"
          subtitle="Customize your VMT data to align with a previous GHG inventory."
          maxHeight="500px"
          pageSize={15}
        />
      </div>
    );
  }, [tableData, modifiedYears, scaledYears, handleDataTableChange, handleRestoreDefaults]);

  // Download handlers
  const handleDownloadPNG = useCallback(() => {
    const viewLabel = chartView === 'total' ? 'Total' : chartView === 'percapita' ? 'Per_Capita' : 'Mode_Share';
    const filename = formatFilename(location, `VMT_Chart_${viewLabel}`);
    downloadChartAsPNG(chartRef, filename);
  }, [chartView, location]);

  const handleDownloadCSV = useCallback(() => {
    const viewLabel = chartView === 'total' ? 'Total' : chartView === 'percapita' ? 'Per_Capita' : 'Mode_Share';
    const filename = formatFilename(location, `VMT_Data_${viewLabel}`);
    downloadDataAsCSV(chartData, filename);
  }, [chartView, location, chartData]);

  // Expose data table content and toggle function to parent ChartLayout
  useEffect(() => {
    if (onDataTableReady && dataTableContent) {
      onDataTableReady(dataTableContent, toggleDataTable);
    }
  }, [dataTableContent, onDataTableReady, toggleDataTable]);

  // Expose download handlers to parent ChartLayout
  useEffect(() => {
    if (onDownloadPNGReady) {
      onDownloadPNGReady(handleDownloadPNG);
    }
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    if (onDownloadCSVReady) {
      onDownloadCSVReady(handleDownloadCSV);
    }
  }, [onDownloadCSVReady, handleDownloadCSV]);

  // Cleanup effect to destroy charts on unmount
  useEffect(() => {
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, []);

  if (loading) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading VMT data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ height: '400px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#dc2626' }}>
        <div>{error}</div>
      </div>
    );
  }

  const chartContent = (
    <div style={{ height: embedOnly ? '380px' : '400px', width: '100%', position: 'relative' }}>
      <canvas ref={chartRef} style={{ height: '100%', width: '100%' }} />
    </div>
  );

  // Return just the chart content - ChartLayout wrapper is handled by Transportation.tsx
  return (
    <div className="vmt-chart-container">
      {chartContent}
    </div>
  );
};
